import {
  Checkbox,
  InfoTooltip,
  Link,
  Pill,
  SearchField,
  ToggleButton,
} from '@wandb/components';
import React, {useMemo, useState} from 'react';

import {MAX_PINNED_RUNS} from '../../../util/constants';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import DOC_URLS from '../../../util/doc_urls';
import {toSorted} from '../../../util/toSorted';
import {maybePluralize} from '../../../util/uihelpers';
import {TruncateTextTooltip} from '../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {usePinnedRunIdsOnly} from '../RunSelectorRunSetContext';
import {type ProjectOption} from './useCrossProjectOptions';
import {useCrossProjectRuns} from './useCrossProjectRuns';

type RunsListProps = {
  selectedProject: ProjectOption;
  selectedRunIds: Set<string>;
  onToggleRun: (runId: string) => void;
  search: string;
  debouncedSearch: string;
  onSearchChange: (search: string) => void;
};

export const RunsList = ({
  selectedProject,
  selectedRunIds,
  onToggleRun,
  search,
  debouncedSearch,
  onSearchChange,
}: RunsListProps) => {
  const pinnedRunIds = usePinnedRunIdsOnly();
  const pinnedRunIdsSet = useMemo(() => new Set(pinnedRunIds), [pinnedRunIds]);
  const remainingSlots = MAX_PINNED_RUNS - selectedRunIds.size;
  const isMaxSelected = remainingSlots <= 0;

  const [isRegexSearch, setIsRegexSearch] = useState(true);
  const {loading, runs, totalCount, runFilterErrorMessage, loadFailed} =
    useCrossProjectRuns(
      selectedProject.internalId,
      debouncedSearch,
      isRegexSearch
    );

  // List already pinned runs first
  const sortedRuns = useMemo(
    () =>
      toSorted(runs, (a, b) => {
        const aPinned = pinnedRunIdsSet.has(a.id) ? 0 : 1;
        const bPinned = pinnedRunIdsSet.has(b.id) ? 0 : 1;
        return aPinned - bPinned;
      }),
    [runs, pinnedRunIdsSet]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-8">
      <div className="shrink-0 font-semibold flex items-center gap-4">
        <span aria-describedby="select-runs-tooltip">Select runs</span>
        <InfoTooltip id="select-runs-tooltip" aria-label="Runs limit info">
          You can select up to{' '}
          {maybePluralize(Math.max(0, remainingSlots), 'more run')}. Selected
          runs are pinned to your workspace, which allows up to{' '}
          {MAX_PINNED_RUNS} pinned runs.
        </InfoTooltip>
      </div>
      <div className="shrink-0 flex gap-8" data-dd-privacy={DD_PRIVACY_ALLOW}>
        <SearchField
          aria-label="Search runs by name or ID"
          placeholder="Search runs by name or ID"
          value={search}
          onChange={onSearchChange}
          fullWidth
          isInvalid={runFilterErrorMessage != null}
        />
        <div>
          <ToggleButton
            aria-label="Toggle regex matching"
            variant="secondary"
            icon="regex"
            isSelected={isRegexSearch}
            onPress={() => setIsRegexSearch(prev => !prev)}
            tooltip={{
              children: `Regex matching is ${isRegexSearch ? 'enabled' : 'disabled'}`,
              placement: 'top end',
            }}
          />
        </div>
      </div>
      {runFilterErrorMessage != null && (
        <p className="shrink-0 text-sm text-red-450 dark:text-red-550">
          {runFilterErrorMessage}{' '}
          <Link
            href={DOC_URLS.searchRuns}
            target="_blank"
            rel="noopener noreferrer">
            See supported syntax
          </Link>
        </p>
      )}
      {loading && (
        <p className="text-moon-500 dark:text-moon-400">Loading runs...</p>
      )}
      {!loading && runFilterErrorMessage == null && runs.length === 0 && (
        <p className="text-moon-500 dark:text-moon-400">
          {loadFailed
            ? 'Failed to load runs. Please try again.'
            : 'No runs found'}
        </p>
      )}
      {!loading && runFilterErrorMessage == null && runs.length > 0 && (
        <>
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {sortedRuns.map(run => {
              const isAlreadyPinned = pinnedRunIdsSet.has(run.id);
              const isChecked = selectedRunIds.has(run.id);
              const isMarkedForUnpin = isAlreadyPinned && !isChecked;
              const isDisabled =
                !isChecked && !isAlreadyPinned && isMaxSelected;
              return (
                <li
                  key={run.id}
                  className="flex shrink-0 items-center gap-8 px-8 py-6 hover:bg-moon-100 dark:hover:bg-moon-800 rounded [&>label]:min-w-0 [&>label]:flex-1">
                  <Checkbox
                    isSelected={isChecked}
                    isDisabled={isDisabled}
                    onPress={() => onToggleRun(run.id)}>
                    <TruncateTextTooltip
                      className="min-w-0 flex-1 text-moon-800 dark:text-moon-200"
                      fullText={run.displayName || run.name}
                      noLink
                      truncationType={TruncationType.End}
                    />
                  </Checkbox>
                  {isAlreadyPinned && (
                    <Pill
                      icon="checkmark2"
                      textValue="Pinned"
                      color={isMarkedForUnpin ? 'moon' : 'green'}
                      tooltip={{
                        children:
                          'This run is currently pinned in the workspace.',
                        placement: 'left',
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {totalCount > runs.length && (
            <p className="shrink-0 text-wb-text-tertiary text-sm">
              Showing {runs.length} of {totalCount.toLocaleString()} runs. Use
              the search bar to narrow results.
            </p>
          )}
        </>
      )}
    </div>
  );
};
